import { Children, createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Box, Card, CardActionArea, IconButton, Tooltip, Typography } from "@mui/material";
import Carousel from "react-multi-carousel";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import type { RelatedEntityItem } from "../../../types";

import "react-multi-carousel/lib/styles.css";
import { isApiMedia, toAsset } from "../../../utilities/mediaAsset";

const MediaCarouselContext = createContext<{
  selectedItemId: number | undefined;
  onItemSelect: (item: RelatedEntityItem) => void;
}>({
  selectedItemId: undefined,
  onItemSelect: () => {}
});

const carouselResponsiveOptions = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 4,
    paritialVisibilityGutter: 16,
    slidesToSlide: 4 // Skip entire page (4 items)
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 3,
    slidesToSlide: 3 // Skip entire page (3 items)
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 2,
    slidesToSlide: 2 // Skip entire page (2 items)
  }
};

// Custom arrow components to prevent prop warnings
const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: "absolute",
      left: 0,
      zIndex: 2,
      color: "primary.main",
      bgcolor: "rgba(255, 255, 255, 0.8)",
      "&:hover": {
        bgcolor: "rgba(255, 255, 255, 1)"
      }
    }}
  >
    <ArrowCircleLeftIcon />
  </IconButton>
);

const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: "absolute",
      right: 0,
      zIndex: 2,
      color: "primary.main",
      bgcolor: "rgba(255, 255, 255, 0.8)",
      "&:hover": {
        bgcolor: "rgba(255, 255, 255, 1)"
      }
    }}
  >
    <ArrowCircleRightIcon />
  </IconButton>
);

export function MediaCarouselProvider({
  children,
  selectedItemId,
  onItemSelect
}: {
  children: ReactNode;
  selectedItemId: number | undefined;
  onItemSelect: (item: RelatedEntityItem) => void;
}) {
  const handleSelect = useCallback(
    (item: RelatedEntityItem) => {
      if (!item) return;
      onItemSelect(item);
    },
    [onItemSelect]
  );

  return <MediaCarouselContext.Provider value={{ selectedItemId, onItemSelect: handleSelect }}>{children}</MediaCarouselContext.Provider>;
}

export function MediaCarouselItem(props: RelatedEntityItem & { url?: string; children?: ReactNode }) {
  const { id, thumbnail, name } = props || {};
  const { selectedItemId, onItemSelect } = useContext(MediaCarouselContext);

  // A fresh upload's thumbnail may not be written yet (404/403). For image media, fall
  // back to the full-resolution image before showing the generic attachment placeholder,
  // as DataItemCard does, so the form's media strip matches the related-items drawer.
  const isImageType = isApiMedia(props)
    ? props.media_kind === "image"
    : String(props.media_type || "")
        .toLowerCase()
        .includes("image");
  const fullImageUrl = isImageType ? props.url || "" : "";
  const [imgSrc, setImgSrc] = useState(thumbnail || fullImageUrl || "");
  const [triedFull, setTriedFull] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setImgSrc(thumbnail || fullImageUrl || "");
    setTriedFull(false);
    setImgBroken(false);
  }, [thumbnail, fullImageUrl]);

  function handleImgError() {
    if (!triedFull && fullImageUrl && imgSrc !== fullImageUrl) {
      setTriedFull(true);
      setImgSrc(fullImageUrl);
      return;
    }
    setImgBroken(true);
  }

  function handleClick() {
    onItemSelect({
      ...toAsset(props),
      id,
      name,
      category: props.category,
      description: props.description,
      entity_description: props.entity_description,
      is_primary: props.is_primary,
      rel_description: props.rel_description,
      media_type: props?.media_type,
      url: props.url
    });
  }

  return (
    <Card
      key={id}
      sx={{
        m: 1,
        position: "relative",
        border: "2px solid",
        borderColor: id === selectedItemId ? "primary.main" : "transparent",
        transition: "transform 0.15s ease-out", // Faster, smoother transition
        minHeight: "6rem", // Reduced from 180px - just enough for 150px image + text + margins
        minWidth: "8.375rem", // Keep this - ensures cards have proper width
        borderRadius: "0.5rem",
        "&:hover": {
          transform: "scale(1.02)" // Reduced from 1.05 to minimize layout shifts
        },
        // Prevent layout thrashing
        willChange: "transform", // Hint browser for GPU acceleration
        backfaceVisibility: "hidden" // Prevent flicker
      }}
      elevation={0}
    >
      <CardActionArea onClick={handleClick}>
        {imgSrc && !imgBroken ? (
          <Box
            component='img'
            src={imgSrc}
            alt={name || ""}
            onError={handleImgError}
            sx={{
              width: "100%",
              height: 150,
              objectFit: "cover",
              display: "block" // Prevent extra spacing
            }}
            loading='lazy' // Improve performance for images
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 150,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100"
            }}
          >
            <AttachFileIcon sx={{ fontSize: 40, color: "grey.500" }} />
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            padding: "0.5rem",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(0deg, #000000cc, transparent)"
          }}
        >
          <Tooltip title={name}>
            <Typography
              variant='body2'
              sx={{
                color: "#fff",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
            >
              {name || ""}
            </Typography>
          </Tooltip>
        </Box>
        {props.children}
      </CardActionArea>
    </Card>
  );
}

export function MediaCarousel({ children }: { children: ReactNode }) {
  const len = Children.count(children);
  if (len <= 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        mt: 2,
        minHeight: "160px", // Reduced from 200px - just enough for cards + margins

        "& .image-carousel-item": {
          paddingRight: "0.5rem" // add right padding between item so items can have space
        }
      }}
    >
      <Carousel
        responsive={carouselResponsiveOptions}
        infinite={false} // Disable infinite scroll
        customLeftArrow={<CustomLeftArrow />}
        customRightArrow={<CustomRightArrow />}
        removeArrowOnDeviceType={["mobile"]}
        ssr={false}
        partialVisible={false}
        itemClass='image-carousel-item'
      >
        {children}
      </Carousel>
    </Box>
  );
}
